import { useState } from 'react';
import { Banner, Button, Card, Dialog, Field, PageHeader, Pill, type Tone } from '@elysian/ui';
import type { Backup } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';

const KIND: Record<Backup['kind'], string> = {
  scheduled: 'Nightly', manual: 'Taken by hand', pre_import: 'Before an import', pre_restore: 'Before a restore',
  pre_vat_filing: 'Before filing VAT', pre_year_end: 'Before closing the year', uploaded: 'Uploaded',
};
const STATUS: Record<Backup['status'], { tone: Tone; label: string }> = {
  queued: { tone: 'info', label: 'Being taken' }, running: { tone: 'info', label: 'Being taken' },
  complete: { tone: 'ok', label: 'Ready' }, failed: { tone: 'danger', label: 'Failed' }, expired: { tone: 'draft', label: 'Expired' },
};

const when = (s: string) => new Intl.DateTimeFormat('en-IE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Dublin' }).format(new Date(s));
const size = (n: number | null | undefined) => (n ? `${(n / 1_000_000).toFixed(1)} MB` : '');

export function Backups() {
  const { source, can, context } = useSession();
  const toast = useToast((s) => s.show);
  const [version, setVersion] = useState(0);
  const data = useAsync(() => source.backups(), [source, version], { keep: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<Backup | null>(null);

  async function takeNow() {
    setBusy(true);
    setError(null);
    try {
      await source.requestBackup('Taken by hand');
      toast('Backup started. It’s ready in a couple of minutes.');
      setVersion((v) => v + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function download(b: Backup) {
    setError(null);
    try {
      const blob = await source.downloadBackup(b.id);
      if (!blob) {
        toast('Demo data has no backup files to download.');
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `elysian-ledger-backup-${(b.data_as_at ?? b.created_at).slice(0, 10)}.elb`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const list = data.state === 'ready' ? data.data : [];
  return (
    <>
      <PageHeader title="Backup and restore" subtitle="A backup every night, kept for 30 days; the first of each month and any you take by hand are kept for a year."
        actions={can('backups.create') ? <Button variant="primary" disabled={busy} onClick={() => void takeNow()}>Back up now</Button> : undefined} />
      {data.state === 'error' && <Banner>Couldn’t load backups: {data.error.message}</Banner>}
      {error && <div className="el-error-box" role="alert" style={{ marginBottom: 16 }}>{error}</div>}
      <Card>
        <div className="el-table-wrap">
          <table className="el-table" aria-label="Backups">
            <thead><tr><th scope="col">Taken</th><th scope="col">Why</th><th scope="col">Status</th><th scope="col" className="el-num">Size</th><th scope="col"><span className="el-vh">Actions</span></th></tr></thead>
            <tbody>
              {list.map((b) => (
                <tr key={b.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{when(b.data_as_at ?? b.created_at)}</td>
                  <td>{KIND[b.kind]}{b.reason && <div className="el-hint">{b.reason}</div>}</td>
                  <td><Pill tone={STATUS[b.status].tone}>{STATUS[b.status].label}</Pill>
                    {b.status === 'complete' && !b.restore_modes?.includes('replace') && <div className="el-hint">Before a VAT filing: copy only</div>}</td>
                  <td className="el-num el-hint">{size(b.size_bytes)}</td>
                  <td className="el-num">
                    {b.status === 'complete' && (
                      <div className="el-match" style={{ justifyContent: 'flex-end' }}>
                        {can('backups.download') && <Button size="sm" variant="ghost" onClick={() => void download(b)}>Download</Button>}
                        {can('backups.restore') && <Button size="sm" onClick={() => setRestoring(b)}>Restore…</Button>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <p className="el-hint" style={{ marginTop: 12 }}>Backup files are encrypted for this organisation; they can only be restored here.</p>
      {restoring && (
        <Restore backup={restoring} orgName={context?.organisation.legal_name ?? ''} onClose={() => setRestoring(null)}
          onStarted={(msg) => { setRestoring(null); toast(msg); }} />
      )}
    </>
  );
}

function Restore({ backup, orgName, onClose, onStarted }: { backup: Backup; orgName: string; onClose: () => void; onStarted: (msg: string) => void }) {
  const { source } = useSession();
  const replaceOk = backup.restore_modes?.includes('replace') ?? false;
  const [mode, setMode] = useState<'copy' | 'replace'>('copy');
  const stamp = when(backup.data_as_at ?? backup.created_at);
  const [newName, setNewName] = useState(`${orgName} (as at ${stamp.split(',')[0]})`);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function go() {
    setBusy(true);
    setError(null);
    try {
      await source.restoreBackup(backup.id, mode === 'copy' ? { mode, new_name: newName.trim() } : { mode, confirm_name: confirm });
      onStarted(mode === 'copy'
        ? `Restoring as “${newName.trim()}”. It appears in your organisations in a few minutes.`
        : 'Restore started. A safety backup is taken first; you’ll get an email when it’s done.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }
  return (
    <Dialog title={`Restore the backup from ${stamp}`} onClose={onClose}
      actions={<><Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant={mode === 'replace' ? 'danger' : 'primary'} disabled={busy || (mode === 'copy' ? !newName.trim() : confirm !== orgName)} onClick={() => void go()}>
          {mode === 'copy' ? 'Restore as a copy' : 'Replace the books'}
        </Button></>}>
      <div className="el-segmented" role="tablist" aria-label="How to restore">
        <button type="button" role="tab" aria-selected={mode === 'copy'} onClick={() => setMode('copy')}>As a separate copy</button>
        <button type="button" role="tab" aria-selected={mode === 'replace'} disabled={!replaceOk} onClick={() => setMode('replace')}>Replace the current books</button>
      </div>
      {mode === 'copy' ? (
        <>
          <p style={{ margin: 0 }}>A new organisation with the books exactly as they were. Nothing in the current books changes. Good for looking something up or comparing.</p>
          <Field id="rs-name" label="Name for the copy"><input id="rs-name" className="el-input" value={newName} onChange={(e) => setNewName(e.target.value)} /></Field>
        </>
      ) : (
        <>
          <p style={{ margin: 0 }}>Everything entered since {stamp} is removed. A safety backup of the books as they are now is taken first, and CertTrack resends anything it created since.</p>
          <Field id="rs-confirm" label={`Type ${orgName} to confirm`}><input id="rs-confirm" className="el-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="off" /></Field>
        </>
      )}
      {!replaceOk && <p className="el-hint" style={{ margin: 0 }}>A VAT return was filed after this backup, so it can only be restored as a copy.</p>}
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}
